import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  fetchQualityStatistics,
  type QualityStatistics,
} from "@/lib/sharepoint-api";
import { Progress } from "@/components/ui/progress";

interface QualityStatsModalProps {
  jobId: string;
  jobName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function QualityStatsModal({
  jobId,
  jobName,
  open,
  onOpenChange,
}: QualityStatsModalProps) {
  const [stats, setStats] = useState<QualityStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && jobId) {
      loadStats();
    }
  }, [open, jobId]);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchQualityStatistics(jobId);
      setStats(data);
    } catch (err: any) {
      setError(err.message || "Failed to load quality statistics");
    } finally {
      setLoading(false);
    }
  };

  const getPercentage = (value: number, total: number) => {
    if (total === 0) return 0;
    return Math.round((value / total) * 100);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Quality Statistics: {jobName}</DialogTitle>
          <DialogDescription>
            Image quality validation results across all configured sites
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
              <p className="text-sm text-gray-500">Loading statistics...</p>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {!loading && !error && stats && (
          <div className="space-y-6">
            {/* Overall Statistics */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="font-semibold mb-4">Overall Statistics</h3>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">Total Processed</p>
                  <p className="text-2xl font-bold">{stats.totals.total}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Not Processed</p>
                  <p className="text-2xl font-bold text-gray-500">
                    {stats.totals.notProcessed}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="bg-green-500">
                        Pass
                      </Badge>
                      <span className="text-sm font-medium">
                        {stats.totals.pass} images
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {getPercentage(stats.totals.pass, stats.totals.total)}%
                    </span>
                  </div>
                  <Progress
                    value={getPercentage(stats.totals.pass, stats.totals.total)}
                    className="h-2"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="default" className="bg-yellow-500">
                        Warning
                      </Badge>
                      <span className="text-sm font-medium">
                        {stats.totals.warning} images
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {getPercentage(stats.totals.warning, stats.totals.total)}%
                    </span>
                  </div>
                  <Progress
                    value={getPercentage(
                      stats.totals.warning,
                      stats.totals.total
                    )}
                    className="h-2"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive">Fail</Badge>
                      <span className="text-sm font-medium">
                        {stats.totals.fail} images
                      </span>
                    </div>
                    <span className="text-sm text-gray-600">
                      {getPercentage(stats.totals.fail, stats.totals.total)}%
                    </span>
                  </div>
                  <Progress
                    value={getPercentage(stats.totals.fail, stats.totals.total)}
                    className="h-2"
                  />
                </div>
              </div>
            </div>

            {/* Per-Site Statistics */}
            {stats.sites.length > 0 && (
              <div>
                <h3 className="font-semibold mb-3">Statistics by Site</h3>
                <div className="space-y-3">
                  {stats.sites.map((site, idx) => (
                    <div
                      key={idx}
                      className="border rounded-lg p-3 bg-white"
                    >
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Site: {site.siteId.substring(0, 8)}...
                      </p>
                      <div className="grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-gray-600">Pass</p>
                          <p className="font-semibold text-green-600">
                            {site.stats.pass}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Warning</p>
                          <p className="font-semibold text-yellow-600">
                            {site.stats.warning}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Fail</p>
                          <p className="font-semibold text-red-600">
                            {site.stats.fail}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-600">Total</p>
                          <p className="font-semibold">{site.stats.total}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stats.sites.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                <p>No quality statistics available yet.</p>
                <p className="text-sm mt-1">
                  Quality validation must be configured and jobs must run before
                  statistics appear.
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
